// src/screens/EventsRevenue.jsx — /revenue/summary, /revenue/events/:id, /revenue/payment-sessions
import "bootstrap-icons/font/bootstrap-icons.css";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";
import { useNavigate } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement);

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");
const PAGE_LIMIT  = 20;
const PALETTE = ["#0d6efd","#198754","#dc3545","#ffc107","#20c997","#6f42c1","#fd7e14","#0dcaf0"];

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token");
  if (t) return String(t).trim();
  try { const u=JSON.parse(localStorage.getItem("user")||"{}"); return String(u?.access_token||u?.token||"").trim(); } catch { return ""; }
}
function getAuthHeaders() {
  const h={Accept:"application/json"}; const t=getBearerToken();
  if (t) h.Authorization=`Bearer ${t}`; return h;
}
async function apiGet(path) {
  if (!EVENTS_BASE) throw new Error("Events API URL not configured.");
  const res  = await fetch(`${EVENTS_BASE}${path}`, { headers:getAuthHeaders(), cache:"no-store" });
  const json = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
  return json;
}
const fmt = (n) => `BTN. ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("en-US",{timeZone:"Asia/Thimphu",month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit",hour12:true}) : "—";
const pad2 = (n) => String(n).padStart(2,"0");
const todayYMD = () => { const d=new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };

const STATUS_COLORS = { success:"success", pending:"warning", failed:"danger", cancelled:"secondary" };

export default function EventsRevenue() {
  useIdleLogout();
  const navigate = useNavigate();

  const [events, setEvents]     = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessTotal, setSessTotal] = useState(0);
  const [sessPage, setSessPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [sessLoading, setSessLoading] = useState(false);
  const [err, setErr]           = useState("");
  const [sessStatus, setSessStatus] = useState("");
  const [organizers, setOrgs]         = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  /* ---- fetch all events (for metrics + charts) ---- */
  const fetchEvents = useCallback(async (from, to) => {
    if (!EVENTS_BASE) { setErr("Events API URL not configured."); return; }
    try {
      setLoading(true); setErr("");
      const url = new URL(`${EVENTS_BASE}/events`);
      if (from) url.searchParams.set("from_date", from);
      if (to)   url.searchParams.set("to_date", to);
      url.searchParams.set("limit", "100");
      url.searchParams.set("page", "1");
      const res  = await fetch(url.toString(), { headers:getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
      setEvents(Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []);
    } catch(e) { setErr(e.message||"Failed to load events."); }
    finally { setLoading(false); }
  }, []);

  /* ---- fetch payment sessions ---- */
  const fetchSessions = useCallback(async (from, to, status, pg) => {
    if (!EVENTS_BASE) return;
    try {
      setSessLoading(true);
      const url = new URL(`${EVENTS_BASE}/revenue/payment-sessions`);
      if (from)   url.searchParams.set("from_date", from);
      if (to)     url.searchParams.set("to_date", to);
      if (status) url.searchParams.set("status", status);
      url.searchParams.set("page", String(pg));
      url.searchParams.set("limit", String(PAGE_LIMIT));
      const res  = await fetch(url.toString(), { headers:getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
      setSessions(Array.isArray(json?.data) ? json.data : []);
      setSessTotal(json?.total ?? 0);
    } catch { setSessions([]); }
    finally { setSessLoading(false); }
  }, []);

  /* ---- fetch organizers ---- */
  const fetchOrgs = useCallback(async () => {
    if (!EVENTS_BASE) return;
    try {
      setOrgsLoading(true);
      const json = await apiGet("/organizers");
      setOrgs(Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []);
    } catch { setOrgs([]); }
    finally { setOrgsLoading(false); }
  }, []);

  useEffect(() => {
    fetchEvents(fromDate, toDate);
    fetchSessions(fromDate, toDate, "", 1);
    fetchOrgs();
  }, []); // eslint-disable-line

  const handleApply = () => {
    setSessPage(1);
    fetchEvents(fromDate, toDate);
    fetchSessions(fromDate, toDate, sessStatus, 1);
  };

  const handleExport = async () => {
    if (!EVENTS_BASE) return;
    try {
      const url = new URL(`${EVENTS_BASE}/revenue/export`);
      if (fromDate) url.searchParams.set("from_date", fromDate);
      if (toDate)   url.searchParams.set("to_date", toDate);
      const res = await fetch(url.toString(), { headers:getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `events_revenue_${todayYMD()}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch(e) { alert(e.message); }
  };

  /* ---- compute metrics from events list ---- */
  const gross    = useMemo(()=>events.reduce((s,e)=>s+Number(e.gross_revenue||0),0),[events]);
  const tickets  = useMemo(()=>events.reduce((s,e)=>s+Number(e.tickets_sold||0),0),[events]);
  const bookings = useMemo(()=>events.reduce((s,e)=>s+Number(e.booking_count||0),0),[events]);
  const avgTicket = tickets > 0 ? gross / tickets : 0;

  /* ---- charts derived from events ---- */
  const catChart = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const k = e.category || "other";
      map[k] = (map[k] || 0) + Number(e.gross_revenue || 0);
    });
    const entries = Object.entries(map).filter(([,v])=>v>0);
    if (!entries.length) return { labels:["No Data"], datasets:[{data:[1],backgroundColor:["#dee2e6"]}] };
    return { labels:entries.map(([k])=>k), datasets:[{data:entries.map(([,v])=>v), backgroundColor:entries.map((_,i)=>PALETTE[i%PALETTE.length])}] };
  }, [events]);

  const payChart = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const k = e.category || "other";
      map[k] = (map[k] || 0) + Number(e.booking_count || 0);
    });
    const entries = Object.entries(map).filter(([,v])=>v>0);
    if (!entries.length) return { labels:["No Data"], datasets:[{data:[1],backgroundColor:["#dee2e6"]}] };
    return { labels:entries.map(([k])=>k), datasets:[{data:entries.map(([,v])=>v), backgroundColor:entries.map((_,i)=>PALETTE[i%PALETTE.length])}] };
  }, [events]);

  const trendChart = useMemo(() => {
    if (!events.length) return null;
    const map = {};
    events.forEach(e => {
      if (!e.start_at) return;
      const d = e.start_at.slice(0, 10);
      map[d] = (map[d] || 0) + Number(e.gross_revenue || 0);
    });
    // only show dates that actually have revenue
    const sorted = Object.entries(map)
      .filter(([, v]) => v > 0)
      .sort(([a], [b]) => a.localeCompare(b));
    if (!sorted.length) return null;
    return {
      labels: sorted.map(([d]) => d),
      datasets: [{
        label: "Revenue (BTN)",
        data: sorted.map(([, v]) => v),
        backgroundColor: "rgba(13,110,253,0.4)",
        borderColor: "#0d6efd",
        borderWidth: 2,
        borderRadius: 6,
      }],
    };
  }, [events]);

  const donutOpts = { responsive:true, maintainAspectRatio:true, layout:{padding:{bottom:10}},
    plugins:{ legend:{position:"bottom",labels:{boxWidth:12,padding:10,font:{size:11}}},
      tooltip:{callbacks:{label:(ctx)=>`${ctx.label}: ${fmt(ctx.raw??0)}`}} } };

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `BTN. ${Number(ctx.raw||0).toLocaleString()}` } },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => `BTN. ${Number(v).toLocaleString()}` },
        grid: { color: "rgba(0,0,0,0.06)" },
      },
      x: {
        ticks: { maxRotation: 30, minRotation: 0 },
        grid: { display: false },
      },
    },
    datasets: { bar: { maxBarThickness: 48, barPercentage: 0.5, categoryPercentage: 0.6 } },
  };

  const sessTotalPages = Math.max(1,Math.ceil(sessTotal/PAGE_LIMIT));
  const handleSessPage = (p) => { setSessPage(p); fetchSessions(fromDate,toDate,sessStatus,p); };

  return (
    <>
      {err && <div className="alert alert-danger">{err}</div>}

      {/* METRICS */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 events-metrics">
        {[
          {label:"Gross Revenue",    value: loading ? "…" : fmt(gross)},
          {label:"Total Bookings",   value: loading ? "…" : bookings.toLocaleString()},
          {label:"Tickets Sold",     value: loading ? "…" : tickets.toLocaleString()},
          {label:"Avg Ticket Value", value: loading ? "…" : fmt(avgTicket)},
        ].map((m,i)=>(
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FILTER ROW */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon"><i className="bi bi-calendar-range"/></span>
          <div className="date-range">
            <input type="date" className="date-pill" value={fromDate} onChange={e=>setFromDate(e.target.value)}/>
            <span className="range-sep">to</span>
            <input type="date" className="date-pill" value={toDate} onChange={e=>setToDate(e.target.value)}/>
          </div>
          <div className="exports ms-auto d-flex gap-2">
            <button className="btn btn-primary" onClick={handleApply}>Apply</button>
            <button className="btn btn-success" onClick={handleExport}>Export CSV</button>
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card chart-card h-100">
            <div className="card-header">Revenue by Category</div>
            <div className="card-body"><Doughnut data={catChart} options={donutOpts}/></div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card chart-card h-100">
            <div className="card-header">Bookings by Category</div>
            <div className="card-body"><Doughnut data={payChart} options={donutOpts}/></div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card chart-card h-100">
            <div className="card-header">Daily Revenue Trend</div>
            <div className="card-body">
              {trendChart
                ? <div style={{height:260,position:"relative"}}><Bar data={trendChart} options={barOpts}/></div>
                : <div style={{height:260,display:"flex",alignItems:"center",justifyContent:"center"}}><p className="text-muted small mb-0">No trend data available.</p></div>}
            </div>
          </div>
        </div>
      </div>

      {/* ORGANIZERS TABLE */}
      <div className="bookings-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Organizers</h5>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{width:"60px"}}/><col style={{width:"200px"}}/><col style={{width:"180px"}}/>
              <col style={{width:"130px"}}/><col style={{width:"80px"}}/><col style={{width:"120px"}}/>
            </colgroup>
            <thead>
              <tr>
                <th>SI.NO</th><th>Name</th><th>Email</th><th>Phone</th><th>Events</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgsLoading && <tr><td colSpan={6} className="py-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2"/>Loading…</td></tr>}
              {!orgsLoading && organizers.length===0 && <tr><td colSpan={6} className="text-center py-4 text-muted">No organizers found.</td></tr>}
              {!orgsLoading && organizers.map((o,i)=>(
                <tr key={o.id}>
                  <td>{i+1}</td>
                  <td className="fw-semibold">{o.name||"—"}</td>
                  <td style={{fontSize:12}}>{o.user?.email||"—"}</td>
                  <td style={{fontSize:13}}>{o.user?.phone||"—"}</td>
                  <td className="mono">{o.event_count??0}</td>
                  <td>
                    <div className="ev-actions">
                      <button className="ev-btn ev-btn-manage" title="Revenue"
                        onClick={()=>navigate(`/services/events/revenue/organizer/${o.id}?tab=revenue`,{state:{orgName:o.name}})}>
                        <i className="bi bi-bar-chart"/>
                      </button>
                      <button className="ev-btn ev-btn-toggle" title="Transactions"
                        onClick={()=>navigate(`/services/events/revenue/organizer/${o.id}?tab=transactions`,{state:{orgName:o.name}})}>
                        <i className="bi bi-clock-history"/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAYMENT SESSIONS TABLE */}
      <div className="bookings-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">BFS Payment Sessions</h5>
          <select className="form-select form-select-sm w-auto" value={sessStatus}
            onChange={e=>{ setSessStatus(e.target.value); setSessPage(1); fetchSessions(fromDate,toDate,e.target.value,1); }}>
            <option value="">All Statuses</option>
            {["pending","success","failed","cancelled"].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{width:"60px"}}/><col style={{width:"170px"}}/><col style={{width:"160px"}}/>
              <col style={{width:"120px"}}/><col style={{width:"100px"}}/><col style={{width:"170px"}}/>
              <col style={{width:"170px"}}/>
            </colgroup>
            <thead>
              <tr>
                <th>SI.NO</th><th>Order No</th><th>BFS Txn ID</th>
                <th>Amount</th><th>Status</th><th>Customer</th><th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {sessLoading && <tr><td colSpan={7} className="py-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2"/>Loading…</td></tr>}
              {!sessLoading && sessions.length===0 && <tr><td colSpan={7} className="text-center py-4 text-muted">No payment sessions found.</td></tr>}
              {!sessLoading && sessions.map((s,i)=>(
                <tr key={s.id}>
                  <td>{(sessPage-1)*PAGE_LIMIT+i+1}</td>
                  <td className="mono" style={{fontSize:12}}>{s.order_no||"—"}</td>
                  <td className="mono" style={{fontSize:12}}>{s.bfs_txn_id||"—"}</td>
                  <td className="mono nowrap amount-cell">{fmt(s.amount)}</td>
                  <td><span className={`badge bg-${STATUS_COLORS[s.status]||"secondary"}`}>{s.status||"—"}</span></td>
                  <td>
                    <div style={{fontSize:13}}>{s.user_name||"—"}</div>
                    <div className="text-muted" style={{fontSize:11}}>{s.user_email||""}</div>
                  </td>
                  <td className="nowrap" style={{fontSize:13}}>{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sessTotalPages>1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2 px-3">
            <small className="text-muted">Page {sessPage} of {sessTotalPages}</small>
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-outline-secondary" disabled={sessPage<=1} onClick={()=>handleSessPage(sessPage-1)}>&lsaquo; Prev</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={sessPage>=sessTotalPages} onClick={()=>handleSessPage(sessPage+1)}>Next &rsaquo;</button>
            </div>
          </div>
        )}
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
